import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import './CreateContent.css';

const TOPICS = {
  8: [
    'Integers', 'Exponents', 'Common Fractions', 'Decimal Fractions',
    'Percentages', 'Ratio and Rate', 'Algebraic Expressions', 'Algebraic Equations',
    'Numeric Patterns', 'Geometry of 2D Shapes', 'Geometry of 3D Objects',
    'Theorem of Pythagoras', 'Area and Perimeter', 'Surface Area and Volume',
    'Data Handling', 'Probability'
  ],
  9: [
    'Whole Numbers', 'Integers', 'Exponents', 'Common Fractions', 'Decimal Fractions',
    'Percentages', 'Financial Mathematics', 'Algebraic Expressions', 'Algebraic Equations',
    'Numeric Patterns', 'Geometric Patterns', 'Functions and Relationships', 'Graphs',
    'Construction of Geometric Figures', 'Geometry of 2D Shapes',
    'Geometry of Straight Lines', 'Geometry of 3D Objects', 'Transformation Geometry',
    'Theorem of Pythagoras', 'Area and Perimeter', 'Surface Area and Volume',
    'Data Handling', 'Probability'
  ],
  10: [
    'Algebra', 'Number Patterns', 'Functions', 'Finance', 'Trigonometry',
    'Euclidean Geometry', 'Analytical Geometry', 'Statistics', 'Probability'
  ],
  11: [
    'Algebra', 'Functions', 'Finance', 'Trigonometry',
    'Euclidean Geometry', 'Analytical Geometry', 'Statistics',
    'Probability', 'Patterns and Sequences'
  ],
  12: [
    'Algebra', 'Functions', 'Finance', 'Trigonometry',
    'Euclidean Geometry', 'Analytical Geometry', 'Statistics',
    'Probability', 'Calculus', 'Patterns and Sequences'
  ]
};

const SUBTOPICS = {
  'Integers': ['Addition', 'Subtraction', 'Multiplication', 'Division', 'Order of Operations'],
  'Exponents': ['Laws', 'Simplification', 'Scientific Notation'],
  'Common Fractions': ['Addition', 'Subtraction', 'Multiplication', 'Division', 'Mixed Numbers'],
  'Decimal Fractions': ['Conversion', 'Operations', 'Rounding'],
  'Percentages': ['Calculations', 'Increase and Decrease', 'Discount', 'VAT'],
  'Algebraic Expressions': ['Simplification', 'Substitution', 'Expansion', 'Factorization'],
  'Algebraic Equations': ['Linear Equations', 'Word Problems', 'Simultaneous Equations'],
  'Numeric Patterns': ['Linear Patterns', 'Quadratic Patterns', 'General Term'],
  'Geometric Patterns': ['Visual Patterns', 'Number Sequences', 'General Rules'],
  'Geometry of 2D Shapes': ['Triangles', 'Quadrilaterals', 'Circles', 'Angle Relationships'],
  'Geometry of 3D Objects': ['Nets', 'Views', 'Cross-Sections', 'Euler\'s Formula'],
  'Theorem of Pythagoras': ['Finding Sides', 'Pythagorean Triples', 'Word Problems'],
  'Area and Perimeter': ['Rectangles', 'Triangles', 'Circles', 'Composite Shapes'],
  'Surface Area and Volume': ['Prisms', 'Cylinders', 'Pyramids', 'Composite Solids'],
  'Data Handling': ['Mean', 'Median', 'Mode', 'Range', 'Graphs'],
  'Probability': ['Counting Principles', 'Probability Rules', 'Venn Diagrams', 'Tree Diagrams'],
  'Financial Mathematics': ['Simple Interest', 'Compound Interest', 'Hire Purchase', 'Exchange Rates'],
  'Ratio and Rate': ['Simplifying Ratios', 'Sharing in Ratio', 'Rate Calculations'],
  'Algebra': ['Equations', 'Inequalities', 'Polynomials', 'Sequences', 'Surds', 'Exponents'],
  'Number Patterns': ['Linear Patterns', 'Quadratic Patterns', 'General Term'],
  'Functions': ['Linear', 'Quadratic', 'Exponential', 'Hyperbolic', 'Inverse Functions'],
  'Finance': ['Simple Interest', 'Compound Interest', 'Annuities', 'Loans', 'Investments'],
  'Trigonometry': ['Ratios', 'Identities', 'Equations', 'Graphs', '2D Problems', '3D Problems'],
  'Euclidean Geometry': ['Circle Theorems', 'Triangles', 'Quadrilaterals', 'Similarity', 'Proportionality'],
  'Analytical Geometry': ['Distance', 'Midpoint', 'Gradient', 'Straight Line', 'Circles'],
  'Statistics': ['Data Analysis', 'Measures of Central Tendency', 'Regression', 'Correlation'],
  'Calculus': ['Limits', 'Differentiation', 'Applications', 'Optimization', 'Rates of Change'],
  'Patterns and Sequences': ['Arithmetic', 'Geometric', 'Sigma Notation', 'Convergence'],
  'Graphs': ['Cartesian Plane', 'Linear Graphs', 'Gradient', 'Equations of Lines'],
  'Functions and Relationships': ['Flow Diagrams', 'Tables', 'Formulae', 'Equations'],
  'Construction of Geometric Figures': ['Bisecting', 'Perpendicular Lines', 'Constructing Triangles'],
  'Geometry of Straight Lines': ['Angle Relationships', 'Parallel Lines', 'Transversals'],
  'Transformation Geometry': ['Reflection', 'Translation', 'Rotation', 'Enlargement'],
  'Whole Numbers': ['Properties', 'Operations', 'Factors and Multiples', 'HCF and LCM']
};

const CreateContent = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('question');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [showTips, setShowTips] = useState(false);

  // Question form state
  const [qForm, setQForm] = useState({
    grade: user?.grade || 10,
    topic: '',
    subtopic: '',
    difficulty: 'medium',
    type: 'numerical',
    questionText: '',
    latex: '',
    correctAnswer: '',
    marks: 2,
    options: [
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false },
      { text: '', isCorrect: false }
    ],
    solutionSteps: [''],
    hints: ['']
  });

  // Worked example form state
  const [weForm, setWEForm] = useState({
    grade: user?.grade || 10,
    topic: '',
    subtopic: '',
    title: '',
    problemText: '',
    problemLatex: '',
    steps: [{ instruction: '', latex: '', explanation: '' }],
    keyInsights: [''],
    commonMistakes: ['']
  });

  const availableTopics = TOPICS[activeTab === 'question' ? qForm.grade : weForm.grade] || [];
  const currentTopic = activeTab === 'question' ? qForm.topic : weForm.topic;
  const availableSubtopics = SUBTOPICS[currentTopic] || [];

  // ---- Question form handlers ----
  const updateQ = (field, value) => {
    setQForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'grade') {
        const newTopics = TOPICS[value] || [];
        updated.topic = newTopics[0] || '';
        updated.subtopic = '';
      }
      if (field === 'topic') {
        updated.subtopic = '';
      }
      return updated;
    });
  };

  const updateOption = (idx, field, value) => {
    setQForm(prev => {
      const opts = [...prev.options];
      if (field === 'isCorrect') {
        opts.forEach((o, i) => { o.isCorrect = i === idx; });
      } else {
        opts[idx] = { ...opts[idx], [field]: value };
      }
      return { ...prev, options: opts };
    });
  };

  const updateArrayField = (formType, field, idx, value) => {
    const setter = formType === 'question' ? setQForm : setWEForm;
    setter(prev => {
      const arr = [...prev[field]];
      arr[idx] = value;
      return { ...prev, [field]: arr };
    });
  };

  const addArrayItem = (formType, field, defaultVal) => {
    const setter = formType === 'question' ? setQForm : setWEForm;
    setter(prev => ({ ...prev, [field]: [...prev[field], defaultVal] }));
  };

  const removeArrayItem = (formType, field, idx) => {
    const setter = formType === 'question' ? setQForm : setWEForm;
    setter(prev => {
      const arr = [...prev[field]];
      if (arr.length <= 1) return prev;
      arr.splice(idx, 1);
      return { ...prev, [field]: arr };
    });
  };

  // ---- Worked example form handlers ----
  const updateWE = (field, value) => {
    setWEForm(prev => {
      const updated = { ...prev, [field]: value };
      if (field === 'grade') {
        const newTopics = TOPICS[value] || [];
        updated.topic = newTopics[0] || '';
        updated.subtopic = '';
      }
      if (field === 'topic') {
        updated.subtopic = '';
      }
      return updated;
    });
  };

  const updateStep = (idx, field, value) => {
    setWEForm(prev => {
      const steps = [...prev.steps];
      steps[idx] = { ...steps[idx], [field]: value };
      return { ...prev, steps };
    });
  };

  const addStep = () => {
    setWEForm(prev => ({
      ...prev,
      steps: [...prev.steps, { instruction: '', latex: '', explanation: '' }]
    }));
  };

  const removeStep = (idx) => {
    setWEForm(prev => {
      if (prev.steps.length <= 1) return prev;
      const steps = [...prev.steps];
      steps.splice(idx, 1);
      return { ...prev, steps };
    });
  };

  // ---- Submit handlers ----
  const submitQuestion = async () => {
    setError(null);
    if (!qForm.questionText.trim()) { setError('Question text is required'); return; }
    if (!qForm.topic) { setError('Please select a topic'); return; }
    if (!qForm.correctAnswer.trim() && qForm.type !== 'multiple-choice') {
      setError('Correct answer is required'); return;
    }
    if (qForm.type === 'multiple-choice') {
      const hasCorrect = qForm.options.some(o => o.isCorrect && o.text.trim());
      const filledOpts = qForm.options.filter(o => o.text.trim());
      if (filledOpts.length < 2) { setError('At least 2 options required for multiple choice'); return; }
      if (!hasCorrect) { setError('Please mark one option as correct'); return; }
    }

    setSubmitting(true);
    try {
      const payload = {
        questionText: qForm.questionText,
        grade: parseInt(qForm.grade),
        topic: qForm.topic,
        subtopic: qForm.subtopic || undefined,
        difficulty: qForm.difficulty,
        type: qForm.type,
        correctAnswer: qForm.type === 'multiple-choice'
          ? qForm.options.find(o => o.isCorrect)?.text || ''
          : qForm.correctAnswer,
        marks: parseInt(qForm.marks) || 2,
        latex: qForm.latex || undefined,
        solutionSteps: qForm.solutionSteps.filter(s => s.trim()),
        hints: qForm.hints.filter(h => h.trim())
      };
      if (qForm.type === 'multiple-choice') {
        payload.options = qForm.options.filter(o => o.text.trim()).map(o => ({
          text: o.text,
          isCorrect: o.isCorrect
        }));
      }
      await api.post('/api/questions/create', payload);
      setSuccess('Question created successfully! It will appear on your Created tab after review.');
      setTimeout(() => navigate(`/profile/${user.username}`), 2000);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create question');
    } finally {
      setSubmitting(false);
    }
  };

  const submitWorkedExample = async () => {
    setError(null);
    if (!weForm.title.trim()) { setError('Title is required'); return; }
    if (!weForm.topic) { setError('Please select a topic'); return; }
    if (!weForm.problemText.trim()) { setError('Problem text is required'); return; }
    const validSteps = weForm.steps.filter(s => s.instruction.trim() || s.latex.trim());
    if (validSteps.length === 0) { setError('At least one solution step is required'); return; }

    setSubmitting(true);
    try {
      const payload = {
        title: weForm.title,
        grade: parseInt(weForm.grade),
        topic: weForm.topic,
        subtopic: weForm.subtopic || undefined,
        description: weForm.problemText,
        problemText: weForm.problemText,
        problemLatex: weForm.problemLatex || undefined,
        steps: weForm.steps.filter(s => s.instruction.trim() || s.latex.trim()).map(s => ({
          instruction: s.instruction,
          latex: s.latex || undefined,
          explanation: s.explanation || undefined
        })),
        keyInsights: weForm.keyInsights.filter(k => k.trim()),
        commonMistakes: weForm.commonMistakes.filter(m => m.trim())
      };
      await api.post('/api/worked-examples/create', payload);
      setSuccess('Worked example created successfully! It will appear on your Created tab after review.');
      setTimeout(() => navigate(`/profile/${user.username}`), 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create worked example');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="create-content-page">
      <div className="create-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <i className="fas fa-arrow-left"></i>
        </button>
        <h1>Create Content</h1>
      </div>

      <div className="create-tabs">
        <button
          className={`create-tab ${activeTab === 'question' ? 'active' : ''}`}
          onClick={() => { setActiveTab('question'); setError(null); setSuccess(null); }}
        >
          <i className="fas fa-question-circle"></i> Question
        </button>
        <button
          className={`create-tab ${activeTab === 'worked-example' ? 'active' : ''}`}
          onClick={() => { setActiveTab('worked-example'); setError(null); setSuccess(null); }}
        >
          <i className="fas fa-book-open"></i> Worked Example
        </button>
      </div>

      {error && <div className="create-alert create-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
      {success && <div className="create-alert create-success"><i className="fas fa-check-circle"></i> {success}</div>}

      {activeTab === 'question' ? (
        <div className="create-form">
          <div className="form-row">
            <div className="form-group">
              <label>Grade</label>
              <select value={qForm.grade} onChange={e => updateQ('grade', parseInt(e.target.value))}>
                {[8,9,10,11,12].map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Difficulty</label>
              <select value={qForm.difficulty} onChange={e => updateQ('difficulty', e.target.value)}>
                <option value="easy">Easy</option>
                <option value="medium">Medium</option>
                <option value="hard">Hard</option>
              </select>
            </div>
            <div className="form-group">
              <label>Marks</label>
              <input type="number" min="1" max="20" value={qForm.marks}
                onChange={e => updateQ('marks', e.target.value)} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Topic</label>
              <select value={qForm.topic} onChange={e => updateQ('topic', e.target.value)}>
                <option value="">Select topic...</option>
                {availableTopics.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Subtopic (optional)</label>
              <select value={qForm.subtopic} onChange={e => updateQ('subtopic', e.target.value)}>
                <option value="">Any</option>
                {availableSubtopics.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Question Type</label>
            <div className="type-selector">
              {['numerical','algebraic','multiple-choice','calculation','short-answer'].map(t => (
                <button key={t} className={`type-btn ${qForm.type === t ? 'active' : ''}`}
                  onClick={() => updateQ('type', t)}>
                  {t.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Question Text <span className="required">*</span></label>
            <textarea rows="4" placeholder="Enter your question here... Use $...$ for inline LaTeX"
              value={qForm.questionText} onChange={e => updateQ('questionText', e.target.value)} />
          </div>

          <div className="form-group">
            <label>LaTeX (optional display math)</label>
            <textarea rows="2" placeholder="e.g. \frac{x^2 + 3x}{2} = 15"
              value={qForm.latex} onChange={e => updateQ('latex', e.target.value)} />
          </div>

          {qForm.type === 'multiple-choice' ? (
            <div className="form-group">
              <label>Options (mark the correct one)</label>
              {qForm.options.map((opt, i) => (
                <div key={i} className="option-row">
                  <input type="radio" name="correctOption" checked={opt.isCorrect}
                    onChange={() => updateOption(i, 'isCorrect', true)} />
                  <input type="text" placeholder={`Option ${String.fromCharCode(65 + i)}`}
                    value={opt.text} onChange={e => updateOption(i, 'text', e.target.value)} />
                </div>
              ))}
            </div>
          ) : (
            <div className="form-group">
              <label>Correct Answer <span className="required">*</span></label>
              <input type="text" placeholder="e.g. 42 or 3x + 5"
                value={qForm.correctAnswer} onChange={e => updateQ('correctAnswer', e.target.value)} />
            </div>
          )}

          <div className="form-group">
            <label>Solution Steps</label>
            {qForm.solutionSteps.map((step, i) => (
              <div key={i} className="array-row">
                <span className="step-num">Step {i + 1}</span>
                <input type="text" placeholder="Describe this step..."
                  value={step} onChange={e => updateArrayField('question', 'solutionSteps', i, e.target.value)} />
                {qForm.solutionSteps.length > 1 && (
                  <button className="remove-btn" onClick={() => removeArrayItem('question', 'solutionSteps', i)}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            ))}
            <button className="add-btn" onClick={() => addArrayItem('question', 'solutionSteps', '')}>
              <i className="fas fa-plus"></i> Add Step
            </button>
          </div>

          <div className="form-group">
            <label>Hints (optional)</label>
            {qForm.hints.map((hint, i) => (
              <div key={i} className="array-row">
                <span className="step-num">Hint {i + 1}</span>
                <input type="text" placeholder="Give a helpful hint..."
                  value={hint} onChange={e => updateArrayField('question', 'hints', i, e.target.value)} />
                {qForm.hints.length > 1 && (
                  <button className="remove-btn" onClick={() => removeArrayItem('question', 'hints', i)}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            ))}
            <button className="add-btn" onClick={() => addArrayItem('question', 'hints', '')}>
              <i className="fas fa-plus"></i> Add Hint
            </button>
          </div>

          <button className="submit-btn" onClick={submitQuestion} disabled={submitting}>
            {submitting ? <><i className="fas fa-spinner fa-spin"></i> Creating...</> : <><i className="fas fa-paper-plane"></i> Create Question</>}
          </button>
        </div>
      ) : (
        <div className="create-form">
          <div className="form-row">
            <div className="form-group">
              <label>Grade</label>
              <select value={weForm.grade} onChange={e => updateWE('grade', parseInt(e.target.value))}>
                {[8,9,10,11,12].map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Topic</label>
              <select value={weForm.topic} onChange={e => updateWE('topic', e.target.value)}>
                <option value="">Select topic...</option>
                {availableTopics.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Subtopic (optional)</label>
              <select value={weForm.subtopic} onChange={e => updateWE('subtopic', e.target.value)}>
                <option value="">Any</option>
                {availableSubtopics.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Title <span className="required">*</span></label>
            <input type="text" placeholder="e.g. Solving Quadratic Equations by Factoring"
              value={weForm.title} onChange={e => updateWE('title', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Problem Text <span className="required">*</span></label>
            <textarea rows="3" placeholder="Describe the problem... Use $...$ for inline LaTeX"
              value={weForm.problemText} onChange={e => updateWE('problemText', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Problem LaTeX (optional display math)</label>
            <textarea rows="2" placeholder="e.g. x^2 - 5x + 6 = 0"
              value={weForm.problemLatex} onChange={e => updateWE('problemLatex', e.target.value)} />
          </div>

          <div className="form-group">
            <label>Solution Steps <span className="required">*</span></label>
            {weForm.steps.map((step, i) => (
              <div key={i} className="step-card">
                <div className="step-card-header">
                  <span className="step-num">Step {i + 1}</span>
                  {weForm.steps.length > 1 && (
                    <button className="remove-btn" onClick={() => removeStep(i)}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
                <input type="text" placeholder="Instruction (e.g. Factor the quadratic)"
                  value={step.instruction} onChange={e => updateStep(i, 'instruction', e.target.value)} />
                <input type="text" placeholder="LaTeX (optional, e.g. (x-2)(x-3) = 0)"
                  value={step.latex} onChange={e => updateStep(i, 'latex', e.target.value)} />
                <input type="text" placeholder="Explanation (optional)"
                  value={step.explanation} onChange={e => updateStep(i, 'explanation', e.target.value)} />
              </div>
            ))}
            <button className="add-btn" onClick={addStep}>
              <i className="fas fa-plus"></i> Add Step
            </button>
          </div>

          <div className="form-group">
            <label>Key Insights</label>
            {weForm.keyInsights.map((ki, i) => (
              <div key={i} className="array-row">
                <input type="text" placeholder="e.g. Always check by substituting back"
                  value={ki} onChange={e => updateArrayField('worked-example', 'keyInsights', i, e.target.value)} />
                {weForm.keyInsights.length > 1 && (
                  <button className="remove-btn" onClick={() => removeArrayItem('worked-example', 'keyInsights', i)}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            ))}
            <button className="add-btn" onClick={() => addArrayItem('worked-example', 'keyInsights', '')}>
              <i className="fas fa-plus"></i> Add Insight
            </button>
          </div>

          <div className="form-group">
            <label>Common Mistakes</label>
            {weForm.commonMistakes.map((cm, i) => (
              <div key={i} className="array-row">
                <input type="text" placeholder="e.g. Forgetting to check both roots"
                  value={cm} onChange={e => updateArrayField('worked-example', 'commonMistakes', i, e.target.value)} />
                {weForm.commonMistakes.length > 1 && (
                  <button className="remove-btn" onClick={() => removeArrayItem('worked-example', 'commonMistakes', i)}>
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            ))}
            <button className="add-btn" onClick={() => addArrayItem('worked-example', 'commonMistakes', '')}>
              <i className="fas fa-plus"></i> Add Common Mistake
            </button>
          </div>

          <button className="submit-btn" onClick={submitWorkedExample} disabled={submitting}>
            {submitting ? <><i className="fas fa-spinner fa-spin"></i> Creating...</> : <><i className="fas fa-paper-plane"></i> Create Worked Example</>}
          </button>
        </div>
      )}

      <p className="create-note">
        <i className="fas fa-info-circle"></i> Your content will be reviewed before appearing in feeds. It will immediately appear on your profile's Created tab.
      </p>

      <div className={`tips-panel ${showTips ? 'tips-open' : ''}`}>
        <button className="tips-toggle" onClick={() => setShowTips(!showTips)}>
          {showTips ? <><i className="fas fa-times"></i> Close Tips</> : <><i className="fas fa-lightbulb"></i> Formatting Tips</>}
        </button>
        
        {showTips && (
          <div className="tips-content">
            <div className="tips-section">
              <h3><i className="fas fa-superscript"></i> Using LaTeX (Math Notation)</h3>
              <p className="tips-intro">Wrap maths in dollar signs to render it beautifully:</p>
              
              <div className="tip-example">
                <div className="tip-label">Inline maths</div>
                <code>Solve $x^2 - 4x + 4 = 0$ for $x$</code>
                <div className="tip-note">Use single <code>$...$</code> within your sentence</div>
              </div>

              <div className="tip-example">
                <div className="tip-label">Display maths (separate LaTeX field)</div>
                <code>\frac{"{x^2 + 3x}"}{"{2}"} = 15</code>
                <div className="tip-note">Use the LaTeX field for standalone equations — no dollar signs needed there</div>
              </div>

              <div className="tip-example">
                <div className="tip-label">Common LaTeX</div>
                <table className="latex-table">
                  <tbody>
                    <tr><td>Fraction</td><td><code>$\frac{"{a}"}{"{b}"}$</code></td></tr>
                    <tr><td>Exponent</td><td><code>$x^{"{2}"}$</code> or <code>$x^2$</code></td></tr>
                    <tr><td>Square root</td><td><code>$\sqrt{"{16}"}$</code></td></tr>
                    <tr><td>Subscript</td><td><code>$T_{"{n}"}$</code></td></tr>
                    <tr><td>Greater/less</td><td><code>$x \geq 5$</code> &nbsp; <code>$x \leq 3$</code></td></tr>
                    <tr><td>Multiply</td><td><code>$3 \times 4$</code></td></tr>
                    <tr><td>Divide</td><td><code>$12 \div 3$</code></td></tr>
                    <tr><td>Not equal</td><td><code>$x \neq 0$</code></td></tr>
                    <tr><td>Plus-minus</td><td><code>$\pm 5$</code></td></tr>
                    <tr><td>Pi</td><td><code>$\pi$</code></td></tr>
                    <tr><td>Theta</td><td><code>$\theta$</code></td></tr>
                    <tr><td>Infinity</td><td><code>$\infty$</code></td></tr>
                    <tr><td>Degrees</td><td><code>$45^{"{\circ}"}$</code></td></tr>
                    <tr><td>Trig</td><td><code>$\sin 30^{"{\circ}"}$</code></td></tr>
                    <tr><td>Log</td><td><code>$\log_{"{2}"} 8$</code></td></tr>
                    <tr><td>Sum</td><td><code>$\sum_{"{i=1}"}^{"{n}"} i$</code></td></tr>
                  </tbody>
                </table>
              </div>

              <div className="tip-example">
                <div className="tip-label">SA conventions</div>
                <ul className="tips-list">
                  <li>Decimals: use <code>$3{","}{14}$</code> for 3,14</li>
                  <li>Coordinates: use semicolons e.g. <code>$(3; -2)$</code></li>
                  <li>Currency: R (Rands), VAT at 15%</li>
                </ul>
              </div>
            </div>

            <div className="tips-section">
              <h3><i className="fas fa-question-circle"></i> Question Tips</h3>
              <ul className="tips-list">
                <li>Keep answers clean — integers or simple decimals work best</li>
                <li>For multiple choice, provide 4 options with plausible distractors</li>
                <li>Include solution steps so others can learn from your question</li>
                <li>Add hints to help students who get stuck</li>
                <li>Set marks to match the CAPS standard (1-2 for easy, 3-5 for hard)</li>
              </ul>
            </div>

            <div className="tips-section">
              <h3><i className="fas fa-book-open"></i> Worked Example Tips</h3>
              <ul className="tips-list">
                <li>Break solutions into clear, numbered steps</li>
                <li>Each step: what to do (instruction), the maths (LaTeX), and why (explanation)</li>
                <li>Add key insights — the "aha!" moments students should remember</li>
                <li>Include common mistakes — what NOT to do and why</li>
                <li>Use descriptive titles e.g. "Factorising Difference of Squares"</li>
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateContent;
